import { createContext, useContext, useState, useEffect } from 'react';
import signalRService from '../services/signalRService';
import { jwtDecode } from 'jwt-decode';
import { profileService, authService } from '../services/api';
import { setAnalyticsUser, clearAnalyticsUser } from '../services/analytics';
import { isTokenExpired } from '../utils/tokenUtils';

const AuthContext = createContext(null);

const getIsAdmin = (token) => {
    try {
        const decoded = jwtDecode(token);
        const roles = decoded['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
        if (!roles) return false;
        return Array.isArray(roles) ? roles.includes('Admin') : roles === 'Admin';
    } catch {
        return false;
    }
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isAdmin, setIsAdmin] = useState(false);

    useEffect(() => {
        const init = async () => {
            const savedToken = localStorage.getItem('token');
            if (!savedToken) { setLoading(false); return; }

            let activeToken = savedToken;
            if (isTokenExpired(savedToken)) {
                try {
                    const { data } = await authService.refresh();
                    activeToken = data.token;
                    localStorage.setItem('token', activeToken);
                } catch {
                    localStorage.removeItem('token');
                    setLoading(false);
                    return;
                }
            }

            setToken(activeToken);
            setIsAdmin(getIsAdmin(activeToken));
            await loadUserProfile();
            await connectSignalR();
        };
        init();
    }, []);

    useEffect(() => {
        const handleForceLogout = () => logout();
        window.addEventListener('auth:logout', handleForceLogout);
        return () => window.removeEventListener('auth:logout', handleForceLogout);
    }, []);

    useEffect(() => {
        const handle = (e) => {
            setToken(e.detail.token);
            setIsAdmin(getIsAdmin(e.detail.token));
        };
        window.addEventListener('auth:tokenRefreshed', handle);
        return () => window.removeEventListener('auth:tokenRefreshed', handle);
    }, []);

    const connectSignalR = async () => {
        try {
            await signalRService.connect();
            console.log('SignalR connected');
        } catch (error) {
            console.error('Failed to connect to SignalR:', error);
        }
    };

    const disconnectSignalR = async () => {
        try {
            await signalRService.disconnect();
            console.log('SignalR disconnected');
        } catch (error) {
            console.error('Failed to disconnect from SignalR:', error);
        }
    };

    const loadUserProfile = async () => {
        try {
            const response = await profileService.getMyProfile();
            setUser(response.data);
            setAnalyticsUser(response.data.id);
        } catch (error) {
            console.error('Error loading profile:', error);
        } finally {
            setLoading(false);
        }
    };

    const login = async (newToken) => {
        localStorage.setItem('token', newToken);
        setToken(newToken);
        setIsAdmin(getIsAdmin(newToken));

        await connectSignalR();

        try {
            const response = await profileService.getMyProfile();
            setUser(response.data);
            setAnalyticsUser(response.data.id);
        } catch (error) {
            console.error('Error loading profile:', error);
        }
    };

    const logout = async () => {
        try {
            await authService.logout();
        } catch {
            // ignore
        }
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
        setIsAdmin(false);
        clearAnalyticsUser();
        await disconnectSignalR();
    };

    const updateUserProfile = (updatedData) => {
        setUser(prev => ({ ...prev, ...updatedData }));
    };

    if (loading) {
        return (
        <div className="page-placeholder">...</div>
    );
    }

    return (
        <AuthContext.Provider value={{ user, token, isAdmin, login, logout, updateUserProfile }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
